import { useState } from "react";
import {
  Button,
  Modal,
  Form,
  Row,
  Col,
  notification,
  Card,
  Progress,
  Divider,
  DatePicker,
} from "antd";
import { EyeOutlined, SendOutlined, EyeInvisibleOutlined, CheckOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { coreApi } from "../../api/calls";
import { fetchBuildingUnitScheduleRules } from "../../api/scheduleRulesApi";
import PartnerSelect from "../Partners/PartnerSelect";
import BuildingUnitSelect from "../Buildings/BuildingUnitSelect";
import ScheduleRuleItem from "./ScheduleRuleItem";

const SendCleaningSchedulesForPartnerModal = ({
  isVisible,
  onCancel,
  handleApiError
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [emailPreview, setEmailPreview] = useState("");

  const [selectedUnits, setSelectedUnits] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [allUnitsData, setAllUnitsData] = useState([]);
  const [additionalUnits, setAdditionalUnits] = useState([]);
  const [additionalUnitsData, setAdditionalUnitsData] = useState([]);
  const [buildingUnitRules, setBuildingUnitRules] = useState({});
  const [isLoadingRules, setIsLoadingRules] = useState(false);
  const [allFreeCleaningChecked, setAllFreeCleaningChecked] = useState(false);

  const loadBuildingUnitsForPartner = async (partnerId) => {
    if (!partnerId) {
      return;
    }

    try {
      setIsLoadingRules(true);
      const response = await coreApi.get(`/buildings/units/all`, {
        params: { partner_id: partnerId }
      });
      
      setAllUnitsData(response.data);

      const currentlySelected = form.getFieldValue('building_unit_ids') || [];
      
      if (currentlySelected.length === 0) {
        setSelectedUnits(response.data);
        const unitIds = response.data.map(unit => unit?.id);
        form.setFieldValue('building_unit_ids', unitIds);
        
        const unitsForRules = response.data.map(unit => ({ id: unit.id, building_unit: unit }));
        await updateRulesListFromSelectedUnits(unitsForRules);
      } else {
        const currentSelectedUnits = response.data.filter(unit =>
          currentlySelected.includes(unit?.id)
        );
        setSelectedUnits(currentSelectedUnits);
        
        const unitsForRules = currentSelectedUnits.map(unit => ({ id: unit.id, building_unit: unit }));
        await updateRulesListFromSelectedUnits(unitsForRules);
      }
    } catch (error) {
      handleApiError && handleApiError(error);
      setAllUnitsData([]);
      setSelectedUnits([]);
    } finally {
        setIsLoadingRules(false);
        setAllFreeCleaningChecked(false);
    }
  };

  const loadBuildingUnitRules = async (buildingUnitId) => {
    if (buildingUnitRules[buildingUnitId]) {
      return buildingUnitRules[buildingUnitId];
    }

    try {
      const rules = await fetchBuildingUnitScheduleRules(buildingUnitId);
      setBuildingUnitRules(prev => ({
        ...prev,
        [buildingUnitId]: rules
      }));
      return rules;
    } catch (error) {
      handleApiError && handleApiError(error);
      return [];
    }
  };

  const handleBulkFreeCleaningToggle = () => {
    const formValues = form.getFieldsValue();
    const rulesList = formValues.rulesList || [];
    
    const newCheckedState = !allFreeCleaningChecked;
    
    const updatedRulesList = rulesList.map(rule => ({
      ...rule,
      freeGeneralCleaning: newCheckedState
    }));
    
    form.setFieldValue('rulesList', updatedRulesList);
    setAllFreeCleaningChecked(newCheckedState);
  };

  // Function to update rules list in form based on selected units
  const updateRulesListFromSelectedUnits = async (units = selectedUnits) => {
    if (units.length === 0) {
      form.setFieldValue('rulesList', []);
      return;
    }

    const formatAddress = (unit) => {
      const streetPart = [unit?.street, unit?.house_number].filter(Boolean).join(" ").trim();
      const cityPart = unit?.city ? `, ${unit.city}` : "";
      const districtPart = unit?.district ? `, ${unit.district}` : "";
      return `${streetPart}${cityPart}${districtPart}`.trim();
    };

    try {
      const allRules = [];

      // Load rules for each selected unit
      for (const unit of units) {
        const unitRules = await loadBuildingUnitRules(unit.id);
        unitRules.forEach(rule => {
          allRules.push({
            ...rule,
            buildingUnitId: unit.id,
            buildingUnitAddress: formatAddress(unit.building_unit),
            // Rule already has proper name from API (like "1x TÝDNĚ", "1x MĚSÍČNĚ")
            name: rule.name,
            uniqueId: `${unit.id}-${rule.id || rule.name}`,
            freeGeneralCleaning: false, // Add for each unit separately
            date_from: rule.date_from || moment().startOf('isoWeek'), // Set default date
            include: true
          });
        });
      }

        form.setFieldValue('rulesList', allRules);

    } catch (error) {
      console.error('Error updating rules list:', error);
      handleApiError && handleApiError(error);
    }
  };

  const handlePartnerChange = async (partner) => {
    setAllUnitsData([]);
    setSelectedUnits([]);
    setAdditionalUnits([]);
    setAdditionalUnitsData([]);
    setBuildingUnitRules({});
    setAllFreeCleaningChecked(false);
    setEmailPreview("");
    setIsButtonDisabled(true);

    form.setFieldValue('rulesList', []);
    form.setFieldValue('building_unit_ids', []);
    form.setFieldValue('additional_building_unit_ids', []);

    setSelectedPartner(partner);

    if (partner?.id) {
      await loadBuildingUnitsForPartner(partner.id);
    }
  };

  const handleBuildingUnitsChange = (selectedUnitIds) => {
    const newSelectedUnits = allUnitsData.filter(unit =>
      selectedUnitIds.includes(unit?.id)
    );
    setSelectedUnits(newSelectedUnits);

    const combinedUnits = [...newSelectedUnits, ...additionalUnits];
    const unitsForRules = combinedUnits.map(unit => ({ id: unit.id, building_unit: unit }));
    updateRulesListFromSelectedUnits(unitsForRules);
  };

  const loadAdditionalUnitData = async (unitId) => {
    try {
      const response = await coreApi.get(`/buildings/units/${unitId}`);
      return response.data;
    } catch (error) {
      handleApiError && handleApiError(error);
      return null;
    }
  };

  const handleAdditionalUnitsChange = async (selectedUnitIds) => {
    try {
      const newAdditionalUnits = [];
      
      for (const unitId of selectedUnitIds) {
        const existingUnit = additionalUnitsData.find(u => u.id === unitId);
        if (existingUnit) {
          newAdditionalUnits.push(existingUnit);
        } else {
          const unitData = await loadAdditionalUnitData(unitId);
          if (unitData) {
            newAdditionalUnits.push(unitData);
            setAdditionalUnitsData(prev => [...prev, unitData]);
          }
        }
      }
      
      setAdditionalUnits(newAdditionalUnits);

      const combinedUnits = [...selectedUnits, ...newAdditionalUnits];
      const unitsForRules = combinedUnits.map(unit => ({ id: unit.id, building_unit: unit }));
      updateRulesListFromSelectedUnits(unitsForRules);
    } catch (error) {
      handleApiError && handleApiError(error);
    }
  };

  const buildPayload = (values) => {
    const { rulesList = [] } = values;

    const allSelectedUnits = [...selectedUnits, ...additionalUnits];
    const building_units = allSelectedUnits.map((unit) => {
      const unitRules = rulesList.filter(rule => rule.buildingUnitId === unit.id);

      // Find the earliest cleaning start date from all rules for this unit
      const earliestDate = unitRules.reduce((earliest, rule) => {
        if (!rule.date_from) return earliest;
        const ruleDate = moment(rule.date_from);
        return !earliest || ruleDate.isBefore(earliest) ? ruleDate : earliest;
      }, null);
      
      // Check if any rule has free general cleaning enabled
      const hasFreeGeneralCleaning = unitRules.some(rule => rule.freeGeneralCleaning);

      return {
        ...unit,
        id: unit.id,
        address: `${unit?.street || ""} ${unit?.house_number || ""}`.trim(),
        city: `${unit?.city || ""}`.trim(),
        district: `${unit?.district || ""}`.trim(),
        cleaning_start_date: earliestDate ? earliestDate.format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
        free_general_cleaning: hasFreeGeneralCleaning
      };
    });

    return {
      partner_id: selectedPartner?.id,
      building_units
    };
  };

  const handleShowSummary = async () => {
    try {
      setIsSubmitting(true);
      
      // Check if at least one building unit is selected
      if (selectedUnits.length === 0 && additionalUnits.length === 0) {
        notification.error({
          message: t("common.validationError"),
          description: t("buildingSetupChanges.validation.requiredBuildingUnit")
        });
        return;
      }
      
      const values = await form.validateFields();
      const payload = buildPayload(values);

      const { data } = await coreApi.post("/partners/send-cleaning-schedules-email/preview", payload);
      setEmailPreview(data?.html || t("emails.mailError"));
      setIsButtonDisabled(false);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    setProgress(0);
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 99) {
          clearInterval(interval);
          return 99;
        }
        return p + 1;
      });
    }, 100);

    try {
      setIsSubmitting(true);

      // Check if at least one building unit is selected
      if (selectedUnits.length === 0 && additionalUnits.length === 0) {
        notification.error({
          message: t("common.validationError"),
          description: t("buildingSetupChanges.validation.requiredBuildingUnit")
        });
        return;
      }

      const values = await form.validateFields();
      const payload = buildPayload(values);

      await coreApi.post("/partners/send-cleaning-schedules-email/send", payload);
      notification.success({ message: t("emails.mailSent") });
      handleClose();
    } catch (error) {
      handleApiError && handleApiError(error);
    } finally {
      setIsSubmitting(false);
      clearInterval(interval);
      setProgress(100);
    }
  };

  const handleClose = () => {
    setEmailPreview("");
    setIsButtonDisabled(true);
    setAllUnitsData([]);
    setSelectedUnits([]);
    setAdditionalUnits([]);
    setAdditionalUnitsData([]);
    setSelectedPartner(null);
    setBuildingUnitRules({});
    setAllFreeCleaningChecked(false);
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      open={isVisible}
      onCancel={handleClose}
      width={1000}
      title={
        <>
          <strong>{t("schedules.informPartnerOptions.sentSchedulesForPartner")}: {" "}</strong>
        </>
      }
      footer={[
        <Button
          key="confirm"
          type="primary"
          icon={
            isSubmitting ? (
              <Progress
                type="circle"
                percent={progress}
                width={24}
                strokeWidth={10}
                showInfo={true}
                style={{ marginRight: 8 }}
                status="active"
              />
            ) : (
              <SendOutlined />
            )
          }
          disabled={isButtonDisabled || isSubmitting}
          onClick={handleSubmit}
        >
          {t("common.confirm")}
        </Button>
      ]}
    >
      <Form form={form} layout="vertical">
        <Row gutter={12}>
          <Col span={24}>
            <PartnerSelect
              label={t("buildingSetupChanges.partners")}
              name="partner_id"
              required
              validationMessage={t("cleanings.validation.partners")}
              initialValue={selectedPartner?.id}
              onChange={handlePartnerChange}
            />
          </Col>
        </Row>

        {selectedPartner?.id && (
          <>
            <Row gutter={12}>
              <Col span={24}>
                <BuildingUnitSelect
                  key={selectedPartner?.id}
                  label={t("buildingSetupChanges.buildingUnit")}
                  name="building_unit_ids"
                  required={false}
                  mode="multiple"
                  form={form}
                  partnerId={selectedPartner?.id}
                  initialValue={selectedUnits}
                  selectAllOnLoad={false}
                  onChange={handleBuildingUnitsChange}
                />
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={24}>
                <BuildingUnitSelect
                  key={`additional-${selectedPartner?.id}`}
                  label={t("schedules.additionalBuildingUnits")}
                  name="additional_building_unit_ids"
                  required={false}
                  mode="multiple"
                  form={form}
                  initialValue={additionalUnits}
                  selectAllOnLoad={false}
                  onChange={handleAdditionalUnitsChange}
                />
              </Col>
            </Row>
          </>
        )}

        {/* Loading state for building units */}
        {selectedPartner && allUnitsData.length === 0 && isLoadingRules && (
          <Row gutter={12}>
            <Col span={24}>
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Progress type="circle" percent={50} size={80} />
                <p>{t("common.loading")}</p>
              </div>
            </Col>
          </Row>
        )}

        <Divider />

        {/* Schedule section for each selected unit */}
        {isLoadingRules && (
          <Row gutter={[6, 6]}>
            <Col span={24}>
              <Progress percent={50} showInfo={false} />
              <p>{t("common.loading")}</p>
            </Col>
          </Row>
        )}

        {(selectedUnits.length > 0 || additionalUnits.length > 0) && !isLoadingRules && (
          <>
            <Row gutter={[12, 6]} align="bottom">
              <Col span={8}>
                <Form.Item label={t("schedules.bulkDateSetting")}>
                  <DatePicker
                    format="YYYY-MM-DD"
                    placeholder={t("schedules.firstCleaning")}
                    style={{ width: "100%" }}
                    allowClear={false}
                    picker="date"
                    disabledDate={(current) => {
                      return current && current.day() !== 1; // Only Mondays
                    }}
                    onChange={(date) => {
                      if (!date) return;
                      
                      const formValues = form.getFieldsValue();
                      const rulesList = formValues.rulesList || [];
                      
                      const updatedRulesList = rulesList.map(rule => ({
                        ...rule,
                        date_from: date
                      }));
                      
                      form.setFieldValue('rulesList', updatedRulesList);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={t("schedules.freeGeneralCleaning")}>
                  <Button
                    type={allFreeCleaningChecked ? "default" : "primary"}
                    icon={allFreeCleaningChecked ? <EyeInvisibleOutlined /> : <CheckOutlined />}
                    onClick={handleBulkFreeCleaningToggle}
                    style={{ width: "100%" }}
                    title={allFreeCleaningChecked ? t("common.unselectAllTooltip") : t("common.selectAllTooltip")}
                  >
                    {allFreeCleaningChecked ? t("common.unselectAll") : t("common.selectAll")}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={[6, 6]}>
              <Col span={24}>
                <Row gutter={[12, 12]}>
                  <Col span={10}>
                    <strong>{t("common.address")}:</strong>
                  </Col>
                  <Col span={6}>
                    <strong>{t("schedules.frequancy")}:</strong>
                  </Col>
                  <Col span={5}>
                    <strong>{t("schedules.firstCleaning")}:</strong>
                  </Col>
                  <Col span={3}>
                    <strong>{t("schedules.freeGeneralCleaning")}:</strong>
                  </Col>
                </Row>
              </Col>
            </Row>
            
            <Row gutter={[6, 6]}>
              <Col span={24}>
              <Form.List name="rulesList">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }, index) => {
                      const formValues = form.getFieldsValue();
                      const ruleData = formValues.rulesList?.[index];
                      const uniqueKey = ruleData?.uniqueId || key;
                      
                      return (
                        <ScheduleRuleItem
                          key={uniqueKey}
                          fieldKey={key}
                          name={name}
                          restField={restField}
                        />
                      );
                    })}
                  </>
                )}
              </Form.List>
              </Col>
            </Row>
          </>
        )}

        {/* Preview button */}
        <Row justify="end">
          <Col>
            <Button
              type="primary"
              onClick={handleShowSummary}
              icon={<EyeOutlined />}
              disabled={isSubmitting || isLoadingRules || (selectedUnits.length === 0 && additionalUnits.length === 0) || !selectedPartner}
              loading={isSubmitting}
            >
              {t("common.continue")}
            </Button>
          </Col>
        </Row>

        {/* Send button */}
        {emailPreview && (
          <Card style={{ marginTop: 24 }}>
            <iframe
              title="Email Preview"
              style={{ width: '100%', height: '500px', border: '1px solid #ccc' }}
              srcDoc={emailPreview}
            />
          </Card>
        )}
      </Form>
    </Modal>
  );
};

export default SendCleaningSchedulesForPartnerModal;

